import type {
  SmsOptOutSource,
  SmsPerformanceSort,
  SmsPerformanceSortKey,
  SmsTrendMetric,
} from "@/types/sms-analytics";

export const NOTIFICATION_CHANNELS = {
  email: "email",
  sms: "sms",
} as const;

export const DEFAULT_NOTIFICATION_CHANNEL = NOTIFICATION_CHANNELS.email;

export const NOTIFICATION_CHANNEL_OPTIONS = [
  { value: NOTIFICATION_CHANNELS.email, label: "Email" },
  { value: NOTIFICATION_CHANNELS.sms, label: "SMS" },
];

export const UNKNOWN_SMS_TYPE = "unknown";

export const SMS_TYPES = {
  welcome: "welcome",
  careplanApproved: "careplan_approved",
  careplanRequiresReview: "careplan_requires_review",
  updatedCareplanRequiresReview: "updated_careplan_requires_review",
  updatedCareplanApproved: "updated_careplan_approved",
  doseOrNonSubscribedChanges: "dose_or_non_subscribed_changes",
  doseOrNonSubscribedChangesAfterClinicianReview:
    "dose_or_non_subscribed_changes_after_clinician_review",
  subscribedProductRemoved: "subscribed_product_removed",
  subscribedProductRemovedAfterClinicianReview: "subscribed_product_removed_after_clinician_review",
  subscribedProductsReplaced: "subscribed_products_replaced",
  subscribedProductsReplacedAfterClinicianReview:
    "subscribed_products_replaced_after_clinician_review",
  noChangesAfterClinicianReview: "no_changes_after_clinician_review",
  driveToSurvey: "drive_to_survey",
  driveToSurveyReminder: "drive_to_survey_reminder",
  howIsPlanWorking: "how_is_plan_working",
  hereToHelp: "here_to_help",
  firstOrderProductsReminder: "first_order_products_reminder",
  secondOrderProductsReminder: "second_order_products_reminder",
  thirdOrderProductsReminder: "third_order_products_reminder",
  resumeProfilingFirst: "resume_profiling_first",
  resumeProfilingSecond: "resume_profiling_second",
  discontinueProduct: "discontinue_product",
  threadMessage: "thread_message",
  unknown: UNKNOWN_SMS_TYPE,
} as const;

export const SMS_TYPE_LABELS: Record<string, string> = {
  [SMS_TYPES.welcome]: "Welcome",
  [SMS_TYPES.careplanApproved]: "Care plan approved",
  [SMS_TYPES.driveToSurvey]: "Drive to survey",
  [SMS_TYPES.driveToSurveyReminder]: "Drive to survey reminder",
  [SMS_TYPES.hereToHelp]: "Here to help",
  [SMS_TYPES.firstOrderProductsReminder]: "First order products reminder",
  [SMS_TYPES.secondOrderProductsReminder]: "Second order products reminder",
  [SMS_TYPES.resumeProfilingFirst]: "Resume profiling, first reminder",
  [SMS_TYPES.resumeProfilingSecond]: "Resume profiling, second reminder",
  [SMS_TYPES.threadMessage]: "Thread message",
  [SMS_TYPES.unknown]: "Unknown SMS",
};

export const SMS_STATUS = {
  sent: "sent",
  failed: "failed",
} as const;

export const SMS_OPT_OUT_SOURCE = {
  simpletexting: "simpletexting",
  app: "app",
  admin: "admin",
} as const;

export const SMS_OPT_OUT_SOURCE_LABELS: Record<SmsOptOutSource, string> = {
  [SMS_OPT_OUT_SOURCE.simpletexting]: "Via SMS",
  [SMS_OPT_OUT_SOURCE.app]: "Via app",
  [SMS_OPT_OUT_SOURCE.admin]: "Via admin",
};

export const NO_LINK_LABEL = "No link in this SMS";

export const SMS_STATUS_WINDOW_DAYS = 30;

export const DEFAULT_SMS_PERFORMANCE_SORT: SmsPerformanceSort = "-sent";

export const SMS_TREND_METRICS = {
  clickRate: { label: "Click rate", kind: "rate", higherIsBetter: true },
  totalClicks: { label: "Total clicks", kind: "count", higherIsBetter: true },
  sent: { label: "SMS sent", kind: "count", higherIsBetter: true },
  failedRate: { label: "Failed rate", kind: "rate", higherIsBetter: false },
} as const;

export const DEFAULT_SMS_TREND_METRIC = "clickRate";

export const SMS_TREND_DETAIL_LABELS: Partial<Record<SmsTrendMetric, [string, string]>> = {
  clickRate: ["clicked", "with a link"],
  failedRate: ["failed", "attempted"],
};

export const SMS_TREND_PERIOD_NOTE =
  "The current period is not over yet, so it is drawn dashed (rates) or lighter (counts).";

export const SMS_TREND_METRIC_HELP: Record<SmsTrendMetric, string> = {
  clickRate:
    "SMS clicked at least once divided by sent SMS that contain a link. Failed SMS and SMS without a link are excluded. Phones and carriers can preview links, which may add a few extra clicks.",
  totalClicks:
    "Sum of link hits. A patient who opens the same link twice counts twice. Phones and carriers can preview links, which may add a few extra clicks.",
  sent: "SMS accepted by SimpleTexting. Delivery to the phone is not reported.",
  failedRate: "Failed SMS divided by sent plus failed SMS.",
};

export const SMS_TREND_METRIC_KEYS = [
  "clickRate",
  "totalClicks",
  "sent",
  "failedRate",
] as const satisfies readonly SmsTrendMetric[];

export const SMS_PERFORMANCE_SORT_KEYS = [
  "smsType",
  "sent",
  "failed",
  "failedRate",
  "clickRate",
  "totalClicks",
  "stops",
] as const satisfies readonly SmsPerformanceSortKey[];
